import React, { useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Button,
  Paper,
  Typography,
  Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PageHeader, SearchFilterBar, StatusChip, LoadingState } from '../components';
import {
  useAlertsPaginated,
  useMarkAlertRead,
  useMarkAllAlertsRead,
  useCheckAlerts,
} from '../hooks/useAlerts';
import { colors, formatDate } from '../constants';

const TYPE_OPTIONS = [
  { value: 'low_stock', label: 'Low Stock' },
  { value: 'expiring_soon', label: 'Expiring' },
  { value: 'expired', label: 'Expired' },
  { value: 'reorder_needed', label: 'Reorder' },
];

const READ_OPTIONS = [{ value: 'true', label: 'Unread Only' }];

function severityIcon(severity) {
  switch (severity) {
    case 'critical':
      return <ErrorOutlineIcon sx={{ color: colors.error }} />;
    case 'warning':
      return <WarningAmberIcon sx={{ color: colors.warning }} />;
    default:
      return <InfoOutlinedIcon sx={{ color: colors.info }} />;
  }
}

export default function Alerts() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [unreadOnly, setUnreadOnly] = useState('');
  const [toast, setToast] = useState(null);

  const params = useMemo(
    () => ({
      limit: 20,
      type: typeFilter || undefined,
      unreadOnly: unreadOnly === 'true' ? true : undefined,
      search: search || undefined,
    }),
    [typeFilter, unreadOnly, search]
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useAlertsPaginated(params);
  const markRead = useMarkAlertRead();
  const markAllRead = useMarkAllAlertsRead();
  const checkAlerts = useCheckAlerts();

  const items = data?.pages.flatMap((p) => p.data) ?? [];

  const showToast = (severity, message) => setToast({ severity, message });

  const handleMarkAll = async () => {
    try {
      await markAllRead.mutateAsync();
      showToast('success', 'All marked read');
    } catch (e) {
      showToast('error', e?.response?.data?.message || 'Something went wrong');
    }
  };

  const handleCheck = async () => {
    try {
      await checkAlerts.mutateAsync();
      showToast('success', 'Alerts checked');
    } catch (e) {
      showToast('error', e?.response?.data?.message || 'Something went wrong');
    }
  };

  return (
    <>
      <PageHeader
        title="Alerts"
        actions={
          <>
            <Button
              variant="outlined"
              onClick={handleMarkAll}
              disabled={markAllRead.isPending}
              sx={{ textTransform: 'none' }}
            >
              Mark All Read
            </Button>
            <Button
              variant="contained"
              onClick={handleCheck}
              disabled={checkAlerts.isPending}
              sx={{ backgroundColor: colors.primary, textTransform: 'none' }}
            >
              Run Check
            </Button>
          </>
        }
      />

      <SearchFilterBar
        searchPlaceholder="Search alerts..."
        search={search}
        onSearchChange={setSearch}
        filters={[
          { label: 'Read', value: unreadOnly, onChange: setUnreadOnly, options: READ_OPTIONS, allLabel: 'All' },
          { label: 'Type', value: typeFilter, onChange: setTypeFilter, options: TYPE_OPTIONS },
        ]}
      />

      {isLoading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: `1px solid ${colors.border}`, borderRadius: 3 }}>
          <Typography sx={{ color: colors.gray500 }}>No alerts</Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {items.map((item) => (
            <Paper
              key={item.uuid}
              elevation={0}
              onClick={() => {
                if (!item.isRead) markRead.mutate(item.uuid);
              }}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                cursor: item.isRead ? 'default' : 'pointer',
                backgroundColor: item.isRead ? '#fff' : '#e3f2fd',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {severityIcon(item.severity)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: item.isRead ? 400 : 700 }}>
                  {item.message}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <StatusChip status={item.type} label={item.type?.replace(/_/g, ' ')} />
                  {item.productSku && (
                    <Typography variant="caption" sx={{ color: colors.gray400 }}>{item.productSku}</Typography>
                  )}
                </Stack>
              </Box>
              <Typography variant="caption" sx={{ color: colors.gray400 }}>
                {item.createdAt ? formatDate(item.createdAt) : '—'}
              </Typography>
            </Paper>
          ))}

          {hasNextPage && (
            <Button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              sx={{ textTransform: 'none', alignSelf: 'center', mt: 1 }}
            >
              {isFetchingNextPage ? 'Loading...' : 'Load more'}
            </Button>
          )}
        </Stack>
      )}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
        {toast ? (
          <MuiAlert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</MuiAlert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
